import {
  BoltIcon,
  CodeBracketIcon,
  ListBulletIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { ObjectID } from "bson";
import clsx from "clsx";
import dayjs from "dayjs";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { PhaseData, useGetPhase } from "src/api/Services/SharedSpace/phases";
import {
  ThreadData,
  ThreadMessageData,
  useCreateThreadInPhase,
  useCreateThreadMessage,
  useGetPhaseThreads,
  useGetThread,
  useGetThreadMessages,
  useUpdateThread,
} from "src/api/Services/Threads/threads";
import { Avatar } from "src/components/ui/Avatar/Avatar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";

import {
  CaretSortIcon,
  FontBoldIcon,
  FontItalicIcon,
  Link2Icon,
  OpenInNewWindowIcon,
  Pencil2Icon,
  StrikethroughIcon,
} from "@radix-ui/react-icons";
import { Editor, Extension } from "@tiptap/core";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Typography from "@tiptap/extension-typography";
import { BubbleMenu, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  ProjectMembers,
  useGetProjectMembers,
} from "src/api/Services/Projects/projects";
import InputHeading from "src/components/ui/Headings/InputHeading";
import { Modal } from "src/components/ui/Modals/Modal";
import ModalHeading from "src/components/ui/Modals/ModalHeading";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import { Checkbox } from "src/components/ui/checkbox";
import { Count } from "src/components/ui/count";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
// import { MenuBar } from "src/components/ui/editor/tiptap-editor";
import { Loading } from "src/components/ui/loading";
import { TextInput } from "src/components/ui/text-input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { useRealmApp } from "src/store/RealmApp";
import { UserResponseObj } from "src/types/Services/general";
import { getTimeDifference } from "src/utils/functions/dateTime";
import { cn } from "src/utils/ui/ui";
import { NewThreadContextProvider } from "./NewThread/NewThreadContextProvider";
import { ChatMessages } from "./thread-elements/messages";

type Props = {
  threads: ThreadData[];
  isInternal?: boolean;
  isSharedSpace?: boolean;
  isEmbed?: boolean;
};

export const Threads = ({
  // threads,
  isInternal = true,
  isSharedSpace = false,
  isEmbed = false,
}: Props) => {
  const { phaseId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");

  const { data: threads } = useGetPhaseThreads(String(phaseId));
  const { data: phase } = useGetPhase(String(phaseId));

  const [isNewThreadModalOpen, setIsNewThreadModalOpen] = useState(false);

  const [activeThreadId, setActiveThreadId] = useState<string | undefined>(
    !!threads && threads?.length > 0 ? threads[0]._id.toString() : undefined
  );
  const [activeThreadName, setActiveThreadName] = useState("");

  function setActiveThread(threadId: string) {
    setActiveThreadId(threadId);
  }

  useEffect(() => {
    if (!!threads) {
      if (!!chatId) {
        const thread = threads.find((t) => t._id.toString() === chatId);
        if (!!thread) {
          setActiveThreadId(thread._id.toString());
          setActiveThreadName(thread.name);
        }
      } else if (threads.length > 0 && !activeThreadId) {
        setActiveThreadId(threads[0]._id.toString());
        setActiveThreadName(threads[0].name);
      }
    }
  }, [threads, chatId]);

  return (
    <NewThreadContextProvider
      isInternal={isInternal}
      isSharedSpace={isSharedSpace}
      isEmbed={isEmbed}
    >
      {/* {isEmbed || isSharedSpace ? (
          <ButtonTabs tabs={tabs} inEmbed={isEmbed} />
        ) : (
          <BasicTabs tabs={tabs} inEmbed={isEmbed} />
        )} */}
      <>
        {!!threads ? (
          <>
            {!!threads.length ? (
              <div className="flex h-full flex-1 overflow-y-auto">
                <div className="flex w-80 flex-col overflow-auto border-r border-gray-200/70">
                  <div className="flex h-16 w-full items-center space-x-2 border-b border-gray-200/70 px-8 pr-6">
                    {!!phase && (
                      <NewChatButton
                        phase={phase}
                        setActiveThread={setActiveThread}
                      />
                    )}
                    {/* <Button
              // size="lg"
              onClick={() => {
                setIsNewThreadModalOpen(true);
              }}
              className="h-8 w-8"
              variant={"secondary"}
            >
              <AdjustmentsHorizontalIcon className="h-3.5 w-3.5 shrink-0" />
            </Button> */}
                  </div>
                  {!!phase && (
                    <NewThreadModal
                      isNewThreadModalOpen={isNewThreadModalOpen}
                      setIsNewThreadModalOpen={setIsNewThreadModalOpen}
                      sharedSpaceId={phase.sharedSpaceId}
                    />
                  )}
                  <div className={clsx("flex-1 overflow-auto pb-6")}>
                    {/* <InputHeading heading="Active Chats" /> */}
                    {phase && (
                      <div className="divide-y divide-gray-200/70 border-b border-gray-200/70">
                        {threads?.map((thread) => (
                          <ThreadCard
                            phase={phase}
                            thread={thread}
                            setActiveThread={setActiveThread}
                            key={thread._id.toString()}
                            activeThreadId={activeThreadId}
                            setActiveThreadName={setActiveThreadName}
                          />
                        ))}
                      </div>
                    )}
                    {/* <div className="space-y-2">
              <InputHeading heading="Task Chats" />
              <div className="space-y-1">
                {threads?.map((thread) => (
                  <ThreadCard
                    thread={thread}
                    setActiveThread={setActiveThread}
                    key={thread._id.toString()}
                    activeThreadId={activeThreadId}
                    setActiveThreadName={setActiveThreadName}
                  />
                ))}
              </div>
            </div> */}
                  </div>
                </div>
                <div className="flex flex-1 flex-col">
                  {!!threads ? (
                    <>
                      {!!activeThreadId ? (
                        <ActiveThread
                          threadId={activeThreadId}
                          threadName={activeThreadName}
                          isInternal={isInternal}
                          key={activeThreadId}
                        />
                      ) : (
                        <div className="flex h-full flex-col items-center justify-center bg-gray-50">
                          <div className="text-center">
                            <div className="text-lg font-medium text-gray-700">
                              No chat to show
                            </div>
                            <div className="text-gray-500">
                              Create a chat first
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex h-full flex-col items-center justify-center bg-gray-50">
                      <Loading />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="mx-auto mt-[10vh] w-fit">
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-t from-gray-200 to-transparent px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
                <div className="flex">
                  <div className="w-20 bg-gradient-to-l from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="w-[420px] border bg-gray-50/70 p-12">
                    <div className="text-lg font-medium">No Chats</div>
                    <div className="pt-4 font-light leading-relaxed text-gray-700">
                      Chats help you keep track of conversations at the project
                      level or task level. Begin by creating a chat.
                    </div>
                    <div className="flex space-x-2.5 pt-7">
                      {!!phase && (
                        <NewChatButton
                          phase={phase}
                          setActiveThread={setActiveThread}
                          isDefaultButton={true}
                        />
                      )}
                      {/* <NewFolderButton
                        phaseId={phaseId}
                        parentFolder={
                          tree.length > 1 ? tree[tree.length - 1].id : null
                        }
                      /> */}
                    </div>
                  </div>
                  <div className="w-20 bg-gradient-to-r from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                </div>
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-b from-gray-200 to-white px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
              </div>
            )}
          </>
        ) : (
          <Loading />
        )}
      </>
    </NewThreadContextProvider>
  );
};

function NewChatButton({
  phase,
  setActiveThread,
  isDefaultButton,
}: {
  phase: PhaseData;
  setActiveThread: (threadId: string) => void;
  isDefaultButton?: boolean;
}) {
  const app = useRealmApp();
  const [isNewChatDialogOpen, setIsNewChatDialogOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [members, setMembers] = useState<
    {
      _id: string;
      name: string;
    }[]
  >([
    {
      _id: app.currentUser?.customData._id,
      name: app.currentUser?.customData.name,
    },
  ]);

  useEffect(() => {
    if (isNewChatDialogOpen) {
      setSubject("");
      setMembers([
        {
          _id: app.currentUser?.customData._id,
          name: app.currentUser?.customData.name,
        },
      ]);
    }
  }, [isNewChatDialogOpen]);
  const createThreadInPhaseMutation = useCreateThreadInPhase();

  useEffect(() => {
    if (createThreadInPhaseMutation.isSuccess) {
      setIsNewChatDialogOpen(false);
    }
  }, [createThreadInPhaseMutation.isSuccess]);

  useEffect(() => {
    if (createThreadInPhaseMutation.data) {
      setActiveThread(createThreadInPhaseMutation.data.id.toString());
    }
  }, [createThreadInPhaseMutation.data]);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: projectMembers } = useGetProjectMembers(phase._id);
  return (
    <Dialog open={isNewChatDialogOpen} onOpenChange={setIsNewChatDialogOpen}>
      <DialogTrigger asChild>
        {isDefaultButton ? (
          <Button
            onClick={() => {
              setIsNewChatDialogOpen(true);
            }}
            // className="flex-1 space-x-2"
            variant={"secondary"}
          >
            <span>New Chat</span>
          </Button>
        ) : (
          <Button
            onClick={() => {
              setIsNewChatDialogOpen(true);
            }}
            className="flex-1 space-x-2"
            variant={"secondary"}
          >
            <PlusIcon className="h-3 w-3" />
            <span>New Chat</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Chat</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-1">
            <InputHeading heading="Name" />
            <TextInput
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
              }}
              className="w-72"
            />
          </div>
          <div className="space-y-1">
            <InputHeading heading="Members" />
            <ChatMembers
              members={members}
              projectMembers={projectMembers ?? { internal: [], external: [] }}
              setMembers={setMembers}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant={"secondary"}
            onClick={() => setIsNewChatDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={createThreadInPhaseMutation.isLoading || !subject.trim()}
            onClick={() => {
              // remove chatId from the URL
              const searchParams = new URLSearchParams(location.search);
              searchParams.delete("chatId");
              navigate({
                pathname: location.pathname,
                search: searchParams.toString(),
              });
              createThreadInPhaseMutation.mutate({
                params: {
                  name: subject,
                  phaseId: new ObjectID(phase._id),
                  userIds: members.map((m) => new ObjectID(m._id)),
                  // tags: [],
                },
              });
            }}
          >
            {createThreadInPhaseMutation.isLoading ? "Creating.." : "Create"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChatMembers({
  members,
  projectMembers,
  setMembers,
}: {
  members: { _id: string; name: string }[];
  projectMembers: ProjectMembers;
  setMembers: (value: { _id: string; name: string }[]) => void;
}) {
  const app = useRealmApp();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          className="h-fit min-h-[36px] w-72 justify-between py-[5px] pl-2"
          // disabled={disabled}
        >
          <div className="flex flex-wrap gap-1">
            {members.map((member) => (
              <Badge key={member._id}>{member.name}</Badge>
            ))}
          </div>
          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
        {/* <Button
            variant={"secondary"}
            className="h-fit min-h-[36px] w-72 justify-between gap-1 pl-1.5"
          >
            {!!customFieldRecord?.value?.length ? (
              <div className="flex flex-wrap gap-1">
                {customFieldRecord.value.map((value) => (
                  <Badge key={value}>{value}</Badge>
                ))}
              </div>
            ) : (
              <div className="px-1.5 font-normal text-gray-300">
                Select values..
              </div>
            )}
            
          </Button> */}
      </PopoverTrigger>
      <PopoverContent
        className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search.." className="h-9" />
          <CommandList>
            <CommandEmpty>No member found.</CommandEmpty>
            <CommandGroup>
              {projectMembers?.internal.map((member) => (
                <CommandItem
                  key={member._id}
                  value={member.name}
                  onSelect={() => {
                    if (member._id === app.currentUser?.customData._id) {
                      return;
                    }
                    if (members.find((m) => m._id === member._id)) {
                      setMembers(members.filter((m) => m._id !== member._id));
                    } else {
                      setMembers([...members, member]);
                    }
                  }}
                  className={clsx(
                    "gap-2",
                    member._id === app.currentUser?.customData._id &&
                      "cursor-not-allowed opacity-50"
                  )}
                >
                  <Checkbox
                    // disabled={member._id === app.currentUser?.customData._id}
                    checked={!!members.find((m) => m._id === member._id)}
                  />
                  <div className="flex items-center space-x-2">
                    <span className="truncate">{member.name}</span>
                  </div>
                </CommandItem>
              ))}
              {projectMembers?.external
                .filter((m) => m.userType === "fullUser")
                .map((member) => {
                  if (member.userType !== "fullUser") return null;
                  return (
                    <CommandItem
                      key={member._id}
                      value={member.name}
                      onSelect={() => {
                        if (member._id === app.currentUser?.customData._id) {
                          return;
                        }
                        if (members.find((m) => m._id === member._id)) {
                          setMembers(
                            members.filter((m) => m._id !== member._id)
                          );
                        } else {
                          setMembers([...members, member]);
                        }
                      }}
                      className={clsx(
                        "gap-2",
                        member._id === app.currentUser?.customData._id &&
                          "cursor-not-allowed opacity-50"
                      )}
                    >
                      <Checkbox
                        // disabled={member._id === app.currentUser?.customData._id}
                        checked={!!members.find((m) => m._id === member._id)}
                      />
                      <div className="flex items-center space-x-2">
                        <span className="truncate">{member.name}</span>
                      </div>
                    </CommandItem>
                  );
                })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ThreadCard({
  thread,
  // setActiveThread,
  activeThreadId,
  setActiveThreadName,
  phase,
}: {
  phase: PhaseData;
  thread: ThreadData;
  setActiveThread: (threadId: string) => void;
  activeThreadId?: string;
  setActiveThreadName: (threadName: string) => void;
}) {
  const { data: projectMembers } = useGetProjectMembers(phase._id);
  const updateThreadMutation = useUpdateThread();
  const { absValue, unit } = getTimeDifference(thread.createdAt, new Date());
  const [searchParams, setSearchParams] = useSearchParams();
  // const activeThreadId = searchParams.get("chatId");
  const navigate = useNavigate();
  const location = useLocation();
  const lastActiveToday = dayjs(thread.updatedAt).isSame(dayjs(), "day");
  const lastActiveYesterday = dayjs(thread.updatedAt).isSame(
    dayjs().subtract(1, "day"),
    "day"
  );
  return (
    <div
      className={clsx(
        "cursor-pointer pt-2 pb-3 pl-8 pr-6 text-sm transition-colors hover:bg-gray-50",
        activeThreadId === thread._id.toString() && "bg-gray-50"
      )}
      onClick={() => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("chatId", thread._id.toString());
        navigate({
          pathname: location.pathname,
          search: searchParams.toString(),
        });
        setActiveThreadName(thread.name);
        if (thread.otherDetails?.unreadMessagesCount > 0) {
          updateThreadMutation.mutate({
            update: {
              isRead: true,
              _id: thread._id,
            },
            _id: thread._id,
          });
        }
      }}
    >
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-3">
          <div
            className={clsx(
              "truncate",
              activeThreadId === thread._id.toString()
                ? "text-black"
                : "text-gray-700"
            )}
          >
            {thread.name}
          </div>
          <div className="whitespace-nowrap text-xs font-light text-gray-500">
            {lastActiveToday
              ? dayjs(thread.updatedAt).format("h:mm A")
              : lastActiveYesterday
              ? "Yesterday"
              : dayjs(thread.updatedAt).format("MMM DD")}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {!projectMembers ? (
              <div className="flex items-center space-x-1">
                <div className="h-6 w-6 animate-pulse rounded bg-gray-200" />
                <div className="h-6 w-6 animate-pulse rounded bg-gray-200" />
              </div>
            ) : (
              <div className="flex items-center -space-x-1">
                {thread.userIds.map((userId) => {
                  const internalMember = projectMembers.internal.find(
                    (m) => m._id?.toString() === userId.toString()
                  );
                  const externalMember = projectMembers.external.find(
                    (m) =>
                      m.userType === "fullUser" &&
                      m._id?.toString() === userId.toString()
                  );
                  return (
                    <div
                      key={userId.toString()}
                      className={clsx(
                        "flex h-[22px] w-[22px] items-center justify-center rounded-full border-[0.5px]  text-[11px] font-light shadow-sm",
                        internalMember
                          ? "border-gray-300 bg-white text-gray-500"
                          : "border-gray-300 bg-gray-200 text-gray-700",
                        activeThreadId === thread._id.toString()
                          ? "border-gray-400"
                          : "border-gray-400/70"
                      )}
                    >
                      {internalMember
                        ? internalMember?.name
                            .toUpperCase()
                            .split(" ")
                            .map((w) => w[0])
                            .join("")
                        : externalMember?.userType === "fullUser"
                        ? externalMember?.name
                            .toUpperCase()
                            .split(" ")
                            .map((w) => w[0])
                            .join("")
                        : externalMember?.email
                            .toUpperCase()
                            .split(" ")
                            .map((w) => w[0])
                            .join("")}
                    </div>
                  );
                })}
              </div>
            )}
            {thread.associations?.taskId && (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger>
                    <div className="flex h-[22px] w-[22px] items-center justify-center rounded-full border-[0.5px] border-primary bg-white">
                      <BoltIcon className="h-3.5 w-3.5 text-primary" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent sideOffset={0} side="right">
                    <span>Task Chat</span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {!!thread.otherDetails.unreadMessagesCount && (
            <Count variant={"primary"}>
              {thread.otherDetails.unreadMessagesCount}
            </Count>
            // <div className="h-5 min-w-[18px] shrink-0 rounded bg-primary-main px-1 py-0.5 text-center text-xs text-white">
            //   <span className="leading-3">
            //     {thread.otherDetails?.unreadMessagesCount}
            //   </span>
            // </div>
          )}
        </div>
        {/* <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 truncate">
          <span
            className={clsx(
              "min-w-0 truncate text-sm",
              activeThreadId === thread._id.toString()
                ? "text-black"
                : "text-gray-600"
            )}
          >
            {thread.name}
          </span>
          
        </div>
        {thread.otherDetails?.unreadMessagesCount > 0 && (
          <div className="h-5 min-w-[18px] shrink-0 rounded bg-primary-main px-1 py-0.5 text-center text-xs text-white">
            <span className="leading-3">
              {thread.otherDetails?.unreadMessagesCount}
            </span>
          </div>
        )}
      </div> */}
      </div>
    </div>
  );
}

function NewThreadModal({
  isNewThreadModalOpen,
  setIsNewThreadModalOpen,
  sharedSpaceId,
}: {
  isNewThreadModalOpen: boolean;
  setIsNewThreadModalOpen: (value: boolean) => void;
  sharedSpaceId: string;
}) {
  const app = useRealmApp();
  const { phaseId } = useParams();
  const [subject, setSubject] = useState("");
  const [members, setMembers] = useState<UserResponseObj[]>([]);
  const createThreadInPhaseMutation = useCreateThreadInPhase();

  useEffect(() => {
    if (createThreadInPhaseMutation.isSuccess) {
      setIsNewThreadModalOpen(false);
    }
  }, [createThreadInPhaseMutation.isSuccess]);

  useEffect(() => {
    if (isNewThreadModalOpen) {
      setSubject("");
      setMembers([]);
    }
  }, [isNewThreadModalOpen]);

  return (
    <Modal
      isOpen={isNewThreadModalOpen}
      closeModal={() => {
        setIsNewThreadModalOpen(false);
      }}
    >
      <div className="w-[600px] space-y-6 p-6">
        <ModalHeading title="New Chat" subtitle="Details for your new chat" />
        <div className="space-y-6">
          <div className="w-[300px]">
            <TextInput
              // name="subject"
              // name="text"
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
              }}
            />
          </div>
          {/* <div className="space-y-2">
            <InputHeading heading="Members" />
            <ThreadMembers
              members={members}
              sharedSpaceId={sharedSpaceId}
              setMembers={setMembers}
            />
          </div> */}
          <div className="flex justify-end">
            <Button
              disabled={
                subject.trim() === "" || createThreadInPhaseMutation.isLoading
              }
              onClick={() => {
                createThreadInPhaseMutation.mutate({
                  params: {
                    name: subject,
                    phaseId: new ObjectID(phaseId),
                    // userIds: members
                    //   .filter(
                    //     (m) =>
                    //       m._id.toString() !==
                    //       app.currentUser.customData._id.toString()
                    //   )
                    //   .map((m) => m._id),
                    userIds: [],
                    // tags: [],
                  },
                });
              }}
            >
              {createThreadInPhaseMutation.isLoading ? "Creating.." : "Create"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// function ThreadMembers({
//   members,
//   sharedSpaceId,
//   setMembers,
// }: {
//   members: UserResponseObj[];
//   sharedSpaceId: string;
//   setMembers: (value: UserResponseObj[]) => void;
// }) {
//   const app = useRealmApp();
//   const { data: projectMembers } = useGetProjectMembers(phase._id);
//   const [query, setQuery] = useState("");
//   const options = {
//     internal: projectMembers?.internal.filter(
//       (m) => m._id.toString() !== app.currentUser.customData._id
//     ),
//     external: projectMembers?.external.filter(
//       (m) => m.userType === "fullUser" && m._id.toString() !== app.currentUser.customData._id
//     ),
//   };
//   const filteredOptions =
//     query === ""
//       ? options
//       :
//       {
//         internal: options.internal.filter(
//           (option) => option.name.toLowerCase().includes(query.toLowerCase())
//         ),
//         external: options.external.filter(
//           (option) =>
//             option.email.toLowerCase().includes(query.toLowerCase())
//         ),
//       }
//       options
//           .filter((o) => o._id.toString() !== app.currentUser.customData._id)
//           .filter((option) =>
//             option.name
//               .toLowerCase()
//               .replace(/\s+/g, "")
//               .includes(query.toLowerCase().replace(/\s+/g, ""))
//           );

//   useEffect(() => {
//     if (sharedSpaceMembers) {
//       setMembers(
//         sharedSpaceMembers.filter(
//           (m) => m._id.toString() === app.currentUser.customData._id
//         )
//       );
//     }
//   }, [sharedSpaceMembers]);
//   return (
//     <>
//       <div className="flex flex-wrap gap-2">
//         {members.map((m) => (
//           <Badge
//             size={"lg"}
//             text={m.name}
//             color={m.type === "internal" ? "gray" : "slate"}
//             Icon={XMarkIcon}
//             clickable={m._id.toString() !== app.currentUser.customData._id}
//             onIconClick={() => {
//               setMembers(
//                 members.filter(
//                   (member) => member._id.toString() !== m._id.toString()
//                 )
//               );
//             }}
//           />
//         ))}
//       </div>
//       <HeadlessCombobox
//         as="div"
//         value={members}
//         onChange={setMembers}
//         disabled={options.length === 0}
//         multiple
//         className={"w-[300px]"}
//         // by={by}
//         // by={(o1, o2) => {

//         // }}
//         // by={by}
//         // by={by}
//       >
//         {/* <HeadlessCombobox.Label className="block text-sm font-medium text-gray-700">Assigned to</HeadlessCombobox.Label> */}
//         {/* <div
//             className={clsx(
//               "relative rounded-md border border-gray-300 shadow-sm bg-white focus:ring-1  disabled:bg-gray-100",
//               isEmbed
//                 ? " focus-within:border-embedPrimary  focus-within:ring-embedPrimary"
//                 : " focus-within:border-primary-main  focus-within:ring-primary-main"
//             )}
//           > */}
//         <div className={"relative"}>
//           {/* <HeadlessCombobox.Input
//                 // value={query}
//                 placeholder={placeholder}
//                 className={clsx(
//                   "border border-red-200 bg-none  py-2 pl-3 pr-10  placeholder:text-gray-300 focus:outline-none  sm:text-sm"
//                 )}
//                 onBlur={() => {
//                   setQuery("");
//                 }}
//                 onChange={(event) => setQuery(event.target.value)}
//                 // displayValue={displayValue}
//               /> */}

//           <HeadlessCombobox.Button className="w-full">
//             <HeadlessCombobox.Input
//               onBlur={() => {
//                 setQuery("");
//               }}
//               autoComplete="off"
//               // value={query}
//               // placeholder={placeholder}
//               className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm placeholder:text-gray-300 focus:border-primary-main focus:outline-none focus:ring-1 focus:ring-primary-main disabled:bg-gray-100 sm:text-sm"
//               onChange={(event) => setQuery(event.target.value)}
//             />
//             <div className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
//               <ChevronUpDownIcon
//                 className="h-5 w-5 text-gray-400"
//                 aria-hidden="true"
//               />
//             </div>
//           </HeadlessCombobox.Button>

//           <HeadlessCombobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
//             {filteredOptions.length === 0 && query !== "" ? (
//               <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
//                 No results
//               </div>
//             ) : (
//               filteredOptions.map((option) => (
//                 <HeadlessCombobox.Option
//                   key={option?._id.toString() ?? ""}
//                   value={option}
//                   className={({ active }) =>
//                     clsx(
//                       "relative mx-1 cursor-pointer select-none rounded-md py-2 pl-3 pr-9 text-gray-900",
//                       // active ? " text-white" : "text-gray-900",
//                       active && "bg-gray-100"
//                       // active && isEmbed && "bg-embedPrimary",
//                       // active && !isEmbed && "bg-primary-600"
//                     )
//                   }
//                 >
//                   {({ active, selected }) => (
//                     <>
//                       <div className="flex items-center space-x-3">
//                         <div
//                           className={clsx(
//                             option.type === "internal"
//                               ? "bg-gray-200"
//                               : "bg-slate-700",
//                             "h-2 w-2 shrink-0 rounded-full"
//                           )}
//                         ></div>
//                         <span
//                           className={classNames(
//                             "block truncate",
//                             selected ? "font-semibold" : ""
//                           )}
//                         >
//                           {option.name}
//                         </span>
//                       </div>

//                       {selected && (
//                         <span
//                           className={classNames(
//                             "absolute inset-y-0 right-0 flex items-center pr-4",
//                             active ? "text-gray-900" : ""
//                             // active
//                             // ? "text-gray-900"
//                             //   : isEmbed
//                             //   ? "text-embedPrimary"
//                             //   : "text-primary-600"
//                           )}
//                         >
//                           <CheckIcon className="h-4 w-4" aria-hidden="true" />
//                         </span>
//                       )}
//                     </>
//                   )}
//                 </HeadlessCombobox.Option>
//               ))
//             )}
//           </HeadlessCombobox.Options>
//         </div>
//       </HeadlessCombobox>
//     </>
//   );
// }

function ActiveThread({
  threadId,
  threadName,
  isInternal,
}: {
  threadId: string;
  threadName: string;
  isInternal: boolean;
}) {
  const { thread } = useGetThread(new ObjectID(threadId));
  const { threadMessages } = useGetThreadMessages(threadId);

  const messagesDiv = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesDiv.current) {
      messagesDiv.current.scrollTop = messagesDiv.current.scrollHeight;
    }
  }, [threadMessages]);

  const updateThreadMutation = useUpdateThread();
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <>
      <div className="flex h-16 items-center justify-between border-b border-gray-200/70 bg-gray-50 px-6 pr-8">
        <div className="font-[450]">{thread?.name ?? threadName}</div>
        <div className="flex items-center space-x-2">
          {!!thread?.associations?.taskId && (
            <Button
              className="space-x-1 pl-[7px] pr-[9px]"
              variant={"secondary"}
              size={"sm"}
              onClick={() => {
                if (thread?.associations?.taskId) {
                  const searchParams = new URLSearchParams(location.search);
                  searchParams.set(
                    "taskId",
                    thread?.associations?.taskId?.toString()
                  );
                  navigate(
                    {
                      pathname: location.pathname,
                      search: searchParams.toString(),
                    },
                    {
                      replace: true,
                    }
                  );
                }
              }}
            >
              <BoltIcon className="h-3.5 w-3.5 text-gray-400" />
              <div>Open Task</div>
            </Button>
          )}
          {/* <Button
            variant={"secondary"}
            size="sm"
            onClick={() => {
              updateThreadMutation.mutate({
                update: {
                  _id: new ObjectID(threadId),
                  isActive: !thread?.isActive,
                },
                _id: new ObjectID(threadId),
              });
            }}
          >
            {thread?.isActive ? "Mark as Complete" : "Mark as Active"}
          </Button> */}

          {!!thread && <ThreadMembersButton thread={thread} />}
        </div>
      </div>

      {!!threadMessages ? (
        <ChatMessages
          messages={threadMessages}
          className="flex-1 overflow-y-auto bg-white pt-3 pb-6"
        />
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <Loading />
        </div>
      )}

      {/* <div className="" ref={messagesDiv}>
        {!!threadMessages ? (
          <>
            {threadMessages.length > 0 ? (
              threadMessages.map((m) => <ThreadMessage message={m} />)
            ) : (
              <div className="text-sm text-gray-400">No messages</div>
            )}
          </>
        ) : (
          <div className="text-gray-400">Loading...</div>
        )}
      </div> */}

      <div className="rounded-b-md p-6 pt-0">
        <div className="w-full">
          <NewMessageEditor threadId={threadId} />
          {/* <textarea
            id="message"
            name="message"
            rows={3}
            autoFocus
            className={
              "block w-full rounded-md border-[1.5px] border-gray-200 p-4 text-gray-700 shadow-sm outline-none ring-0 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-0"
            }
            onKeyDown={(event) => {
              if (event.key === "Enter" && message.trim() !== "") {
                event.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Message.."
            value={message}
            onChange={(e) => {
              // if (e.target.value)
              setMessage(e.target.value);
            }}
          /> */}
          {/* <div className="w-full rounded-b-md border border-t-0 px-4 py-2">
            Toolbox
          </div> */}
        </div>
      </div>
    </>
  );
}

export function NewMessageEditor({ threadId }: { threadId: string }) {
  // useHotkeys(
  //   "mod+enter",
  //   (e) => {
  //     e.preventDefault();
  //     e.stopPropagation();
  //     if (editor?.isFocused && !!editor?.state.doc.textContent.trim().length) {
  //       sendMessage();
  //     }
  //   },
  //   {
  //     // enableOnFormTags: [""],
  //     enableOnContentEditable: true,
  //     // enabled: () => !!editor?.isFocused,
  //   }
  // );

  const app = useRealmApp();
  const createThreadMessageMutation = useCreateThreadMessage();
  const { thread } = useGetThread(new ObjectID(threadId));

  const sendMessage = (editorInstance: Editor | null) => {
    if (!editorInstance) return;
    if (!editorInstance?.state.doc.textContent.trim().length) return;
    const user = thread?.otherDetails.users?.find(
      (user) => user._id.toString() === app.currentUser?.id
    );

    createThreadMessageMutation.mutate({
      params: {
        threadId: new ObjectID(threadId),
        text: editorInstance?.getHTML(),
      },
      metadata: {
        type: user?.type || "internal",
      },
    });
    editorInstance?.commands.clearContent();
    editorInstance?.commands.focus("start");
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: false,
        heading: false,
        horizontalRule: false,
        // code: false,
      }),
      // Code.extend({
      //   renderHTML() {
      //     return ["code", 0];
      //   },
      // }),
      Typography,
      Placeholder.configure({
        placeholder: "Message...",
      }),
      Link.configure({
        openOnClick: false,
        validate: (href) => /^https?:\/\//.test(href),
      }),
      Extension.create({
        addKeyboardShortcuts() {
          return {
            "Mod-Enter": () => {
              if (!this.editor) return false;
              sendMessage(this.editor);
              return true; // Prevents default behavior
            },
          };
        },
      }),
      // Link.configure({
      //   openOnClick: true,
      // }),
    ],
    editorProps: {
      attributes: {
        class:
          "text-sm prose max-w-full placeholder:text-gray-500 outline-none ring-0 focus:outline-none focus:ring-0 border-none focus:border-none",
      },
    },
    autofocus: true,
    content: "",
    editable: true,
  });

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [urlErrorText, setUrlErrorText] = useState("");

  useEffect(() => {
    editor?.commands.focus();
  }, [threadId]);
  // useEffect(() => {
  //   if (editor) {
  //     editor.commands.setContent(content);
  //   }
  // }, [content, setContent]);

  const removeLink = useCallback(() => {
    editor?.chain().focus().extendMarkRange("link").unsetLink().run();
    closeModal();
  }, [editor, closeModal]);

  const saveLink = useCallback(() => {
    if (url) {
      editor
        ?.chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url, target: "_blank" })
        .run();
    } else {
      editor?.chain().focus().extendMarkRange("link").unsetLink().run();
    }
    editor?.commands.blur();
    closeModal();
  }, [editor, url, closeModal]);

  function closeModal() {
    setIsLinkModalOpen(false);
    setUrl("");
  }

  if (!editor) {
    return null;
  }

  return (
    <div
      onClick={() => {
        editor?.commands.focus();
      }}
      className="flex min-h-[152px] flex-col overflow-hidden rounded-lg border transition-all focus-within:border-gray-300"
    >
      <div className="flex items-center space-x-1 border-b border-gray-200/50 bg-gray-50/70 px-4 py-2.5">
        {editor && (
          <MenuBar
            editor={editor}
            setIsLinkModalOpen={setIsLinkModalOpen}
            setUrl={setUrl}
          />
        )}
      </div>
      <div className="flex-1 px-4 pt-2.5">
        <EditorContent editor={editor} />
      </div>
      <div className="flex items-center justify-between py-2.5 pl-4 pr-2.5">
        <div>
          {/* <PaperClipIcon className="h-3.5 w-3.5 text-gray-400" /> */}
        </div>
        <div>
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  onClick={() => {
                    sendMessage(editor);
                  }}
                  disabled={!editor?.state.doc.textContent.trim().length}
                >
                  Send
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left" sideOffset={8}>
                <span>Press ⌘ + Enter to send</span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      {!!editor && (
        <>
          <BubbleMenu
            pluginKey="bubbleMenuLink"
            tippyOptions={{ duration: 150 }}
            editor={editor}
            shouldShow={({ editor, from, to }) => {
              // only show the bubble menu for links.
              return from === to && editor.isActive("link");
            }}
          >
            <div className="-mb-[6px] flex items-center space-x-0.5 rounded border-[0.5px] border-gray-300 bg-white p-0.5">
              <button
                onClick={() => {
                  setUrl(editor.getAttributes("link").href);
                  setIsLinkModalOpen(true);
                }}
                className="flex w-fit space-x-1 rounded-sm px-1.5 py-[3px] text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                <Pencil2Icon className="h-4 w-4" />
                <span>Edit</span>
              </button>
              <button
                className="flex w-fit space-x-1 rounded-sm px-1.5 py-[3px] text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                onClick={() => {
                  window.open(editor.getAttributes("link").href, "_blank");
                }}
              >
                <OpenInNewWindowIcon className="h-4 w-4" />
                <span>Open</span>
              </button>
            </div>
          </BubbleMenu>
          <BubbleMenu
            pluginKey="bubbleMenuNotLink"
            tippyOptions={{ duration: 150 }}
            editor={editor}
            shouldShow={({ editor, view, state, oldState, from, to }) => {
              // only show the bubble menu for links.
              return (
                from !== to &&
                !editor.isActive("link") &&
                !editor.isActive("horizontalRule")
              );
            }}
          >
            <div className="-mb-1 flex items-center justify-center rounded border-[0.5px] border-gray-300 bg-white p-0.5">
              <button
                onClick={() => {
                  setIsLinkModalOpen(true);
                }}
                className="flex h-5 w-5 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                <Link2Icon className="h-4 w-4" />
              </button>
            </div>
          </BubbleMenu>
          <Dialog open={isLinkModalOpen} onOpenChange={setIsLinkModalOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editor.isActive("link") ? "Edit Link" : "Insert Link"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1 py-3">
                  <InputHeading heading="URL" />
                  <TextInput
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                  <p className="text-xs text-red-500">{urlErrorText}</p>
                </div>
                <div className="flex justify-between">
                  <div>
                    {editor.isActive("link") && (
                      <Button
                        variant={"secondary"}
                        onClick={() => {
                          removeLink();
                        }}
                        className="flex w-8 items-center justify-center p-0"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <Button
                    variant={"secondary"}
                    onClick={() => {
                      if (!url) {
                        setUrlErrorText("URL cannot be empty");
                      } else if (
                        !url.startsWith("http://") &&
                        !url.startsWith("https://")
                      ) {
                        setUrlErrorText(
                          "URL should start with http:// or https://"
                        );
                      } else {
                        saveLink();
                        setUrlErrorText("");
                      }
                    }}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

const MenuBar = ({
  editor,
  setIsLinkModalOpen,
  setUrl,
}: {
  editor: Editor;
  setIsLinkModalOpen: (open: boolean) => void;
  setUrl: (url: string) => void;
}) => {
  return (
    <div className="flex items-center space-x-2">
      <MenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        className={editor.isActive("bold") ? "is-active" : ""}
      >
        <FontBoldIcon className="h-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className={editor.isActive("italic") ? "is-active" : ""}
      >
        <FontItalicIcon className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        className={editor.isActive("strike") ? "is-active" : ""}
      >
        <StrikethroughIcon className="h-4 w-4" />
      </MenuButton>
      {/* <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={editor.isActive("heading", { level: 1 }) ? "is-active" : ""}
      >
        <span className="font-mono">H</span>
      </MenuButton> */}
      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive("bulletList") ? "is-active" : ""}
      >
        <ListBulletIcon className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive("orderedList") ? "is-active" : ""}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          // className="bi bi-list-ol"
          viewBox="0 0 16 16"
        >
          <path
            fill-rule="evenodd"
            d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5z"
          />
          <path d="M1.713 11.865v-.474H2c.217 0 .363-.137.363-.317 0-.185-.158-.31-.361-.31-.223 0-.367.152-.373.31h-.59c.016-.467.373-.787.986-.787.588-.002.954.291.957.703a.595.595 0 0 1-.492.594v.033a.615.615 0 0 1 .569.631c.003.533-.502.8-1.051.8-.656 0-1-.37-1.008-.794h.582c.008.178.186.306.422.309.254 0 .424-.145.422-.35-.002-.195-.155-.348-.414-.348h-.3zm-.004-4.699h-.604v-.035c0-.408.295-.844.958-.844.583 0 .96.326.96.756 0 .389-.257.617-.476.848l-.537.572v.03h1.054V9H1.143v-.395l.957-.99c.138-.142.293-.304.293-.508 0-.18-.147-.32-.342-.32a.33.33 0 0 0-.342.338v.041zM2.564 5h-.635V2.924h-.031l-.598.42v-.567l.629-.443h.635V5z" />
        </svg>
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={editor.isActive("codeBlock") ? "is-active" : ""}
      >
        <CodeBracketIcon className="h-4 w-4" />
      </MenuButton>
      <MenuButton
        onClick={() => {
          const url = editor.getAttributes("link").href;
          if (url) {
            setUrl(url);
          }
          setIsLinkModalOpen(true);
        }}
        className={editor.isActive("link") ? "is-active" : ""}
      >
        <Link2Icon className="h-4 w-4" />
      </MenuButton>
    </div>
  );
};

type ButtonProps = {} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const MenuButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <button
        tabIndex={-1}
        className={cn(
          "text-gray-400 transition-colors hover:text-gray-600",
          className
        )}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    );
  }
);

export function ThreadMembersButton({
  thread,
}: // isInternal,
{
  thread: ThreadData;
  // isInternal: boolean;
}) {
  const numberOfMembers = thread.otherDetails.users?.length ?? 0;
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <>
      <div className="flex items-center space-x-1">
        <Dialog>
          <DialogTrigger asChild>
            <Button variant={"ghost"} className="px-1 hover:bg-gray-200/50">
              <div className="flex items-center -space-x-1">
                {thread.otherDetails.users?.map((u) => {
                  return (
                    <div
                      className={clsx(
                        "flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 text-xs font-light",
                        u.type === "internal"
                          ? "bg-white text-gray-500"
                          : "bg-gray-200 text-gray-700"
                      )}
                    >
                      {u?.name
                        .toUpperCase()
                        .split(" ")
                        .map((w) => w[0])
                        .join("")}
                    </div>
                  );
                })}
              </div>
              {/* <UsersIcon className="h-3.5 w-3.5 shrink-0" /> */}
              {/* <span>Members</span> */}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Members</DialogTitle>
              <DialogDescription>
                Only members part of this chat will be able to send messages and
                receive notifications
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 pt-2">
              {!!thread.phaseId && (
                <AddMembersCombobox
                  thread={thread}
                  phaseId={thread.phaseId.toString()}
                />
              )}
              <Members thread={thread} />
            </div>
          </DialogContent>
        </Dialog>
        {/* {thread.otherDetails?.users?.slice(0, 3).map((u) => {
          return (
            <div
              key={u.toString()}
              className={clsx(
                "flex h-6 w-6 items-center justify-center rounded border text-xs shadow-sm",
                u?.type === "internal"
                  ? "border-gray-300 text-gray-400"
                  : "border-gray-300 bg-gray-200 text-gray-600"
              )}
            >
              {u?.name
                .toUpperCase()
                .split(" ")
                .map((w) => w[0])
                .join("")}
            </div>
          );
        })}
        {numberOfMembers > 3 && (
          <div className="flex h-6 w-6 items-center justify-center rounded border border-gray-300 bg-white p-1 text-gray-400">
            +{numberOfMembers - 3}
          </div>
        )} */}
        {/* {thread.userIds.map((userId) => {
          const member = sharedSpaceMembers.find(
            (m) => m._id?.toString() === userId.toString()
          );
          return (
            <div
              key={userId.toString()}
              className={clsx(
                "flex h-6 w-6 items-center justify-center rounded border text-xs shadow-sm",
                member?.type === "internal"
                  ? "border-gray-300 text-gray-400"
                  : "border-gray-300 bg-gray-200 text-gray-600"
              )}
            >
              {member?.name
                .toUpperCase()
                .split(" ")
                .map((w) => w[0])
                .join("")}
            </div>
          );
        })} */}
      </div>
      {/* <div
        className="flex -space-x-2"
        // onClick={() => {
        //   setIsModalOpen(true);
        // }}
      >
        {thread.otherDetails?.users?.slice(0, 3).map((u) => (
          <Avatar name={u.name} color="gray" size="sm" />
        ))}
        {numberOfMembers > 3 && (
          <div className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-400 bg-white p-1 text-gray-400">
            +{numberOfMembers - 3}
          </div>
        )}
      </div> */}
      {/* <Modal
        isOpen={isModalOpen}
        closeModal={() => {
          setIsModalOpen(false);
        }}
      >
        <div className="min-h-[400px] min-w-[500px] space-y-6 p-6">
          <ModalHeading title="Members" />
          <div className="space-y-2">
            {thread.otherDetails?.users?.map((u) => (
              <div className="flex items-center space-x-3">
                <div
                  className={clsx(
                    u.type === "internal" ? "text-gray-600" : "text-slate-700"
                  )}
                >
                  {u.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal> */}
    </>
  );
}

function AddMembersCombobox({
  thread,
  phaseId,
}: {
  thread: ThreadData;
  phaseId: string;
}) {
  const { data: projectMembers } = useGetProjectMembers(phaseId);
  const [open, setOpen] = useState(false);
  const filteredMembers = {
    internal: projectMembers?.internal.filter(
      (im) =>
        !thread.userIds.map((u) => u.toString()).includes(im._id.toString())
    ),
    external: projectMembers?.external.filter(
      (em) =>
        em.userType === "fullUser" &&
        !thread.userIds.map((u) => u.toString()).includes(em._id.toString())
    ),
  };
  // const filteredMembers =
  //   sharedSpaceMembers?.filter((m) => {
  //     return !thread.userIds
  //       .map((u) => u.toString())
  //       .includes(m._id.toString());
  //   }) ?? [];
  const updateThreadMutation = useUpdateThread();
  return (
    <div className="flex items-center space-x-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          asChild
          disabled={
            !filteredMembers.internal?.length &&
            !filteredMembers.external?.length
          }
        >
          <Button variant="secondary" role="combobox" aria-expanded={open}>
            <PlusIcon className="mr-2 h-4 w-4 shrink-0" />
            <span>Add Member</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-fit min-w-[200px] max-w-[700px] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search members..." className="h-9" />
            <CommandList>
              <CommandEmpty>No member found.</CommandEmpty>
              <CommandGroup>
                {filteredMembers.internal?.map((m) => (
                  <CommandItem
                    key={m._id.toString()}
                    value={m.name}
                    onSelect={() => {
                      // to do : add user to thread
                      updateThreadMutation.mutate({
                        update: {
                          _id: thread._id,
                          userIds: [...thread.userIds, new ObjectID(m._id)],
                        },
                        _id: thread._id,
                      });
                      setOpen(false);
                    }}
                    className="gap-2"
                  >
                    <div className="flex items-center space-x-2">
                      <span className="truncate">{m.name}</span>
                    </div>
                  </CommandItem>
                ))}
                {filteredMembers.external?.map((m) => {
                  if (m.userType === "fullUser") {
                    return (
                      <CommandItem
                        key={m._id.toString()}
                        value={m.name}
                        onSelect={() => {
                          if (thread.userIds) {
                            updateThreadMutation.mutate({
                              update: {
                                _id: thread._id,
                                userIds: [
                                  ...thread.userIds,
                                  new ObjectID(m._id),
                                ],
                              },
                              _id: thread._id,
                            });
                          }
                          setOpen(false);
                        }}
                        className="gap-2"
                      >
                        <div className="flex items-center space-x-2">
                          <span className="truncate">{m.name}</span>
                        </div>
                      </CommandItem>
                    );
                  }
                  return null;
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {/* {!!sharedSpaceMembers && filteredMembers.length === 0 && (
        <div>
          <span className="text-xs text-gray-400">
            All members have been added.
          </span>
        </div>
      )} */}
    </div>
  );
}

function Members({
  thread,
}: // isInternal,
{
  thread: ThreadData;
  // isInternal: boolean;
}) {
  const updateThreadMutation = useUpdateThread();
  return (
    <div>
      <div className="border-b px-2 pb-2">
        <InputHeading heading="Member" />
      </div>
      {thread.otherDetails.users?.map((m) => {
        return (
          <div
            key={m._id.toString()}
            className="relative flex h-9 items-center justify-between border-b px-2 text-sm text-gray-500 last:border-b-0"
          >
            <div>{m.name}</div>
            {/* {isInternal && ( */}
            <ButtonToRemoveMemberFromChat thread={thread} member={m} />
            {/* )} */}
          </div>
        );
      })}
    </div>
  );
}

function ButtonToRemoveMemberFromChat({
  thread,
  member,
}: {
  thread: ThreadData;
  member: UserResponseObj;
}) {
  const updateThreadMutation = useUpdateThread();
  return (
    <Button
      className="h-fit"
      variant={"secondaryLink"}
      disabled={
        updateThreadMutation.isLoading || updateThreadMutation.isSuccess
      }
      onClick={() => {
        updateThreadMutation.mutate({
          update: {
            _id: thread._id,
            userIds: thread.userIds.filter(
              (u) => u.toString() !== member._id.toString()
            ),
          },
          _id: thread._id,
        });
      }}
    >
      {"Remove"}
    </Button>
  );
}

const ThreadMessage = ({ message }: { message: ThreadMessageData }) => {
  return (
    <div className="flex space-x-2">
      <div className="shrink-0">
        <Avatar name={message.user.name} color="gray" rounded="md" />
      </div>
      <div>
        <div className="flex items-center space-x-2 text-xs font-medium">
          <div className="text-gray-600">{message.user.name}</div>
          <span className="text-xs font-medium text-gray-400">
            {dayjs(message.createdAt).format("h:mm A, MMM DD")}
          </span>
        </div>
        <div className="mt-1 text-sm text-gray-700">{message.text}</div>
      </div>
    </div>
  );
};
